import { Controller } from "@hotwired/stimulus"

// Inline rig editing on the admin photography schedule (/djobs?type=photography).
//
// The Rigs and Photog cells each stack one line per rig. Clicking a value swaps
// in a <select> — never a text input: a rig letter and a photographer are closed
// sets, so they are picked, not typed. The options are cloned from two page-level
// <template>s rather than inlined into every cell (see _rig_assignment_cells).
//
// Adding, changing or removing a rig moves three cells of the row at once (Rigs,
// Photog AND Vehicle) and can create or clear a clash with another job on the
// same date. So the server answers every change with the cells for EVERY job on
// that date plus the date's conflict banner, and we paint all of them — a stale
// badge on the other half of a double-booking is exactly what #4720 exists to
// prevent.
export default class extends Controller {
  static values = { djobId: Number }

  editName(event) {
    this._openSelect(event.currentTarget, "rig-name-options", "name")
  }

  editPhotographer(event) {
    this._openSelect(event.currentTarget, "rig-photographer-options", "photographer_id")
  }

  add() {
    this._send(`/djobs/${this.djobIdValue}/rig_assignments`, "POST")
  }

  remove(event) {
    const rigId = event.currentTarget.dataset.rigId
    if (!window.confirm("Remove this rig from the job?")) return

    this._send(`/djobs/${this.djobIdValue}/rig_assignments/${rigId}`, "DELETE")
  }

  // Swap the clicked value for a dropdown seeded with the current value.
  // Committing on `change` (not blur alone) keeps it to a single click-and-pick.
  _openSelect(valueEl, templateId, field) {
    if (valueEl.dataset.editing === "true") return

    const template = document.getElementById(templateId)
    if (!template) return

    const select = document.createElement("select")
    select.className = "djob-rig-select"
    select.innerHTML = template.innerHTML
    select.value = valueEl.dataset.rawValue || ""

    let committed = false
    const commit = () => {
      if (committed) return
      committed = true

      const value = select.value
      if (value === (valueEl.dataset.rawValue || "")) {
        this._close(valueEl, select)
        return
      }

      this._send(
        `/djobs/${this.djobIdValue}/rig_assignments/${valueEl.dataset.rigId}`,
        "PATCH",
        { field, value }
      )
    }

    select.addEventListener("change", commit)
    select.addEventListener("blur", commit)
    select.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        committed = true
        this._close(valueEl, select)
      }
    })

    valueEl.dataset.editing = "true"
    valueEl.hidden = true
    valueEl.insertAdjacentElement("afterend", select)
    select.focus()
  }

  _close(valueEl, select) {
    select.remove()
    valueEl.hidden = false
    delete valueEl.dataset.editing
  }

  _send(url, method, body = null) {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    this.element.classList.add("djob-rig-busy")

    fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrf || ""
      },
      body: body ? JSON.stringify(body) : null
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((payload) => this._paint(payload))
      .catch((error) => {
        // No optimistic repaint here, unlike the note cells: a failed rig write
        // that LOOKED like it saved would leave the schedule showing crew who
        // aren't booked. Say so and leave the page showing server truth.
        console.error("Rig assignment save failed:", error)
        window.alert("That rig change did not save. Refresh the page to see the current schedule.")
      })
      .finally(() => this.element.classList.remove("djob-rig-busy"))
  }

  // Repaint every cell the server sent — including rows this controller doesn't
  // own, which is the point: their conflict badges may have just changed.
  _paint(payload) {
    ;(payload.cells || []).forEach((cell) => {
      ["rigs", "photographers", "vehicles"].forEach((kind) => {
        const target = document.querySelector(
          `[data-djob-id="${cell.djob_id}"][data-rig-cell="${kind}"]`
        )
        if (target && cell[kind] != null) target.innerHTML = cell[kind]
      })
    })

    if (payload.banner) {
      const banner = document.querySelector(
        `[data-conflict-banner-date="${payload.banner.date}"]`
      )
      if (banner) banner.innerHTML = payload.banner.html
    }
  }
}
